import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { 
  Box, Typography, Grid, Paper, Button, 
  FormControl, InputLabel, Select, MenuItem, 
  CircularProgress, Alert, Stack 
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import ReportGenerator from '../../../components/ReportCardGenerator';
import generateComplianceReport from '../../../utils/complianceService';
import { UserContext } from '../../../contexts/UserContext';

const REPORT_TYPES = [
  { value: 'GDPR', label: 'GDPR Compliance' },
  { value: 'FERPA', label: 'FERPA Compliance' },
  { value: 'HIPAA', label: 'HIPAA Compliance' },
  { value: 'COPPA', label: 'COPPA Compliance' },
  { value: 'CUSTOM', label: 'Custom Report' }
];

export default function ComplianceReportsPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [reportType, setReportType] = useState('GDPR');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reportData, setReportData] = useState(null);

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('security');
  }, [user]);

  const handleGenerateReport = async (params) => {
    if (!canManage) return;
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await generateComplianceReport({
        reportType,
        ...params
      });

      setReportData(response.data);
      setSuccess(t('complianceReports.success', 'Report generated successfully!'));
    } catch (err) {
      setError(t('complianceReports.error', 'Failed to generate report: ') + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Typography variant="h4" gutterBottom>
        {t('complianceReports.title', 'Compliance Reports')}
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }} elevation={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('complianceReports.reportType', 'Report Type')}</InputLabel>
              <Select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                label={t('complianceReports.reportType', 'Report Type')}
              >
                {REPORT_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <ReportGenerator 
              reportType={reportType} 
              onGenerate={canManage ? handleGenerateReport : undefined}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            {t('complianceReports.generating', 'Generating report...')}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && reportData && (
        <Stack spacing={2}>
          <Alert severity="success">
            {success}
          </Alert>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('complianceReports.reportSummary', 'Report Summary')}
            </Typography>
            <Typography variant="body1">
              {t('complianceReports.generatedOn', 'Generated on')}: {new Date(reportData.generated_at).toLocaleString()}
            </Typography>
            <Typography variant="body1">
              {t('complianceReports.schoolsIncluded', 'Schools included')}: {reportData.schools_count}
            </Typography>
            <Typography variant="body1">
              {t('complianceReports.usersIncluded', 'Users included')}: {reportData.users_count}
            </Typography>

            <Button 
              variant="contained"
              startIcon={<DescriptionIcon />}
              sx={{ mt: 2 }}
              href={reportData.download_url}
              download
              disabled={!canManage}
            >
              {t('complianceReports.downloadReport', 'Download Full Report')}
            </Button>
          </Paper>
        </Stack>
      )}
    </Box>
  );
}